<script setup>
// One metric of the assistant (phase 4.5): the kit's StatTile, and the change against the previous period as a
// Badge in the tile's corner — green when the change is good for this metric, red when it is bad, neutral otherwise
// (`trendGood`, from the page's resolveTrendGood). PROVISÓRIO: the kit's StatTile has no variation yet (R2-7-9:
// the kit first); when it has, the badge goes and the tile says it. A tile the admin can open (the drilldown) is a
// button for the keyboard too.
import { computed } from 'vue';
import StatTile from 'dashboard/components-next/polaris/cards/StatTile.vue';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';

const props = defineProps({
  label: { type: String, required: true },
  value: { type: String, default: '-' },
  trend: { type: String, default: '' },
  hint: { type: String, default: '' },
  // null = neutral, true = good direction, false = bad direction
  trendGood: { type: Boolean, default: null },
  clickable: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['click']);

const trendVariant = computed(() => {
  if (props.trendGood === null) return 'secondary';
  return props.trendGood ? 'success' : 'destructive';
});

// The tile opens the drilldown; the StatTile's ⓘ button inside it only shows its tooltip. (The ⓘ is a button
// inside a role="button" tile: a proper fix needs the shared StatTile to take the action itself — follow-up.)
const onActivate = event => {
  if (!props.clickable) return;
  if (event?.target?.closest?.('[data-stat-tile] button')) return;
  emit('click');
};
</script>

<template>
  <div
    class="relative rounded-polaris-xl"
    :class="
      clickable
        ? 'cursor-pointer outline-none transition-shadow hover:shadow-md focus-visible:ring-[3px] focus-visible:ring-polaris-ring/50'
        : ''
    "
    :role="clickable ? 'button' : undefined"
    :tabindex="clickable ? 0 : undefined"
    data-metric-card
    @click="onActivate"
    @keydown.enter.self.prevent="onActivate"
    @keydown.space.self.prevent="onActivate"
  >
    <StatTile :label="label" :value="value" :info="hint" :loading="loading" />
    <Badge
      v-if="trend && !loading"
      :variant="trendVariant"
      class="absolute bottom-4 end-4 tabular-nums"
    >
      {{ trend }}
    </Badge>
  </div>
</template>
